import React, { memo, useMemo, useRef, forwardRef } from 'react';
import { cn } from '../../utils/cn';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

// Generate a stable unique ID outside of the component
let idCounter = 0;
const generateId = () => `input-${++idCounter}`;

const Input = memo(forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helperText,
  className,
  id,
  ...props
}, ref) => {
  // Use a ref to store the generated ID so it's stable across renders
  const idRef = useRef<string | null>(null);

  // Memoize the input ID to prevent regeneration on every render
  const inputId = useMemo(() => {
    if (id) return id;
    if (!idRef.current) {
      idRef.current = generateId();
    }
    return idRef.current;
  }, [id]);

  return (
    <div className="input-group">
      {label && (
        <label
          htmlFor={inputId}
          className="input-label"
        >
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={inputId}
        className={cn(
          "input",
          error && "input-error",
          className
        )}
        {...props}
      />
      {error && (
        <p className="input-error-text">{error}</p>
      )}
      {helperText && !error && (
        <p className="input-helper-text">{helperText}</p>
      )}
    </div>
  );
}));

Input.displayName = 'Input';

export { Input };
export default Input;
